import { ResourceDeleteDialog } from '@/components/crud/ResourceDeleteDialog'
import { ResourceFormDialog } from '@/components/crud/ResourceFormDialog'
import { ActionButton } from '@/components/record-sheet/ActionButton'
import { ActionDialog } from '@/components/record-sheet/ActionDialog'
import { RelatedSection } from '@/components/record-sheet/RelatedSection'
import { useRecordSheet } from '@/components/record-sheet/context'
import {
  TONE_CLASS,
  actionIcon,
  btnPrimary,
  btnSecondary,
  iconBtn
} from '@/components/record-sheet/styles'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@/components/ui/sheet'
import { useResourceSchema } from '@/hooks/useResourceSchema'
import { queryClient } from '@/lib/query'
import {
  type RecordSheetData,
  type SheetAction,
  type SheetPhoto,
  type SheetSection,
  fetchRecordSheet
} from '@/lib/sheet'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowUpRight,
  ChevronDown,
  Maximize2,
  Minimize2,
  Pencil,
  Trash2,
  X
} from 'lucide-react'
import { useState } from 'react'

interface Props {
  resourceKey: string
  id: string
  onClose: () => void
}

const PRIMARY_ACTIONS = 2
const card =
  'rounded-[36px] border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900'
// Soft block used by the compact (AI team) layout: tinted, no border, so the panel reads as one page.
const block = 'rounded-[24px] bg-neutral-100 p-5 dark:bg-neutral-900'

// AI team records (drafts, escalations, runs, briefs…) have no photos and are worked
// through, not browsed, so they get a compact work panel instead of the profile layout.
const isCompact = (resourceKey: string) => resourceKey.startsWith('super_agents__')

/**
 * Right-side profile for any admin record, read top to bottom like a public
 * profile page: cover + photo, name, stats, readable sections, then related
 * lists (collapsed, paginated) and the admin history.
 * Everything comes from `GET r/<key>/<id>/sheet/`.
 */
export function RecordSheet({ resourceKey, id, onClose }: Props) {
  const [expanded, setExpanded] = useState(false)
  const [action, setAction] = useState<SheetAction | null>(null)
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const sheet = useQuery({
    queryKey: ['record-sheet', resourceKey, id],
    queryFn: () => fetchRecordSheet(resourceKey, id)
  })
  const schema = useResourceSchema(resourceKey).data
  const data = sheet.data
  const compact = isCompact(resourceKey)
  const canEdit = Boolean(schema && data)

  function refreshEverywhere() {
    queryClient.invalidateQueries({ queryKey: ['record-sheet'] })
    queryClient.invalidateQueries({ queryKey: ['admin-resource-list'] })
    queryClient.invalidateQueries({ queryKey: ['admin-resource-stats'] })
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className={cn(
          'gap-0 overflow-hidden border-l border-neutral-200 p-0 shadow-2xl dark:border-neutral-800',
          compact ? 'bg-white dark:bg-black' : 'bg-neutral-50 dark:bg-neutral-950',
          'data-[side=right]:w-full',
          expanded
            ? 'data-[side=right]:sm:max-w-[100vw]'
            : cn(
                compact
                  ? 'data-[side=right]:sm:max-w-[min(640px,94vw)]'
                  : 'data-[side=right]:sm:max-w-[min(1100px,94vw)]',
                'sm:rounded-l-[36px]'
              )
        )}
      >
        <TopBar
          data={data}
          compact={compact}
          expanded={expanded}
          canEdit={canEdit}
          onToggleExpand={() => setExpanded((v) => !v)}
          onClose={onClose}
          onAction={setAction}
          onEdit={() => setEditing(true)}
          onDelete={() => setDeleting(true)}
        />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {sheet.isLoading && (compact ? <CompactSkeleton /> : <ProfileSkeleton />)}

          {sheet.error && (
            <div className="p-6">
              <div className={cn(card, 'border-red-200 text-center dark:border-red-900/40')}>
                <AlertCircle className="mx-auto size-8 text-red-500" />
                <p className="mt-3 text-sm font-semibold text-neutral-900 dark:text-white">
                  Couldn't load this
                </p>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {sheet.error instanceof Error ? sheet.error.message : String(sheet.error)}
                </p>
                <button type="button" className={`${btnSecondary} mt-4`} onClick={() => sheet.refetch()}>
                  Try again
                </button>
              </div>
            </div>
          )}

          {data && compact && (
            <div className="mx-auto max-w-3xl space-y-4 px-5 pb-8 pt-6 sm:px-7">
              <CompactHeader
                data={data}
                canEdit={canEdit}
                onAction={setAction}
                onEdit={() => setEditing(true)}
                onDelete={() => setDeleting(true)}
              />

              {data.stats.length > 0 && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {data.stats.map((s) => (
                    <div key={s.label} className="rounded-[24px] bg-neutral-100 px-4 py-3 dark:bg-neutral-900">
                      <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">{s.label}</p>
                      <p className="mt-0.5 truncate text-lg font-bold tabular-nums text-neutral-900 dark:text-white">
                        {s.value}
                      </p>
                      {s.hint && <p className="truncate text-[11px] text-neutral-400">{s.hint}</p>}
                    </div>
                  ))}
                </div>
              )}

              {data.sections.map((s) => (
                <SectionCard key={s.title} section={s} compact />
              ))}

              {data.tabs.length > 0 && (
                <div className="space-y-3 pt-2">
                  <h3 className="px-1 text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    Linked
                  </h3>
                  {data.tabs.map((t) => (
                    <RelatedSection
                      key={t.id}
                      resourceKey={resourceKey}
                      id={data.id}
                      tab={t}
                      actions={data.actions.filter((a) => a.tab === t.id)}
                      onAction={setAction}
                    />
                  ))}
                </div>
              )}

              <History data={data} />
            </div>
          )}

          {data && !compact && (
            <div className="mx-auto max-w-5xl space-y-5 p-5 sm:p-8">
              <Hero data={data} />

              {data.stats.length > 0 && (
                <div className="grid grid-cols-2 overflow-hidden rounded-[36px] border border-neutral-200 bg-white sm:grid-cols-3 lg:grid-cols-6 dark:border-neutral-800 dark:bg-neutral-900">
                  {data.stats.map((s) => (
                    <div key={s.label} className="px-5 py-4">
                      <p className="truncate text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">
                        {s.value}
                      </p>
                      <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">{s.label}</p>
                      {s.hint && <p className="truncate text-[11px] text-neutral-400">{s.hint}</p>}
                    </div>
                  ))}
                </div>
              )}

              <Sections sections={data.sections} />

              {data.tabs.length > 0 && (
                <div className="space-y-3 pt-3">
                  <h3 className="px-2 text-lg font-bold text-neutral-900 dark:text-white">
                    Everything linked to {data.title}
                  </h3>
                  {data.tabs.map((t) => (
                    <RelatedSection
                      key={t.id}
                      resourceKey={resourceKey}
                      id={data.id}
                      tab={t}
                      actions={data.actions.filter((a) => a.tab === t.id)}
                      onAction={setAction}
                    />
                  ))}
                </div>
              )}

              <History data={data} />
            </div>
          )}
        </div>

        {action && data && (
          <ActionDialog action={action} resourceKey={resourceKey} id={data.id} onClose={() => setAction(null)} />
        )}
        {schema && data && (
          <ResourceFormDialog
            open={editing}
            onOpenChange={setEditing}
            schema={schema}
            initial={data.record}
            onSaved={refreshEverywhere}
          />
        )}
        {schema && data && (
          <ResourceDeleteDialog
            open={deleting}
            onOpenChange={setDeleting}
            schema={schema}
            ids={[data.id]}
            onDeleted={() => {
              setDeleting(false)
              refreshEverywhere()
              onClose()
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------

function TopBar({
  data,
  compact,
  expanded,
  canEdit,
  onToggleExpand,
  onClose,
  onAction,
  onEdit,
  onDelete
}: {
  data: RecordSheetData | undefined
  compact: boolean
  expanded: boolean
  canEdit: boolean
  onToggleExpand: () => void
  onClose: () => void
  onAction: (a: SheetAction) => void
  onEdit: () => void
  onDelete: () => void
}) {
  if (compact) {
    return (
      <div className="flex items-center gap-2 border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
        <div className="min-w-0 flex-1">
          <SheetTitle className="truncate text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            {data?.subtitle || 'Details'}
          </SheetTitle>
          <SheetDescription className="sr-only">Full details and admin actions</SheetDescription>
        </div>
        <button
          type="button"
          className={cn(iconBtn, 'hidden sm:inline-flex')}
          aria-label={expanded ? 'Shrink panel' : 'Full screen'}
          onClick={onToggleExpand}
        >
          {expanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
        </button>
        <button type="button" className={iconBtn} aria-label="Close" onClick={onClose}>
          <X className="size-4" />
        </button>
      </div>
    )
  }

  const general = data?.actions.filter((a) => !a.tab) ?? []
  const safe = general.filter((a) => !a.danger)
  const primary = safe.slice(0, PRIMARY_ACTIONS)
  const more = safe.slice(PRIMARY_ACTIONS)
  const danger = general.filter((a) => a.danger)

  return (
    <div className="flex items-center gap-2 border-b border-neutral-200 bg-white px-5 py-3 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="min-w-0 flex-1">
        <SheetTitle className="truncate text-sm font-semibold text-neutral-900 dark:text-white">
          {data?.title ?? 'Loading…'}
        </SheetTitle>
        <SheetDescription className="sr-only">Full details and admin actions</SheetDescription>
      </div>
      {primary.map((a) => (
        <ActionButton key={a.id} action={a} onClick={() => onAction(a)} />
      ))}
      {canEdit && (
        <button type="button" className={btnSecondary} onClick={onEdit}>
          <Pencil className="size-4" /> Edit
        </button>
      )}
      {data && (more.length > 0 || danger.length > 0 || canEdit) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={btnSecondary}>
              More <ChevronDown className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-64 rounded-[36px] border border-neutral-200 bg-white p-2 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900"
          >
            {more.map((a) => (
              <MenuAction key={a.id} action={a} onSelect={() => onAction(a)} />
            ))}
            {more.length > 0 && (
              <DropdownMenuSeparator className="my-1.5 bg-neutral-200 dark:bg-neutral-800" />
            )}
            {danger.map((a) => (
              <MenuAction key={a.id} action={a} onSelect={() => onAction(a)} />
            ))}
            {canEdit && (
              <DropdownMenuItem
                onSelect={onDelete}
                className="gap-2 rounded-full px-3 py-2 font-semibold text-red-600 focus:bg-red-50 focus:text-red-600 dark:text-red-400 dark:focus:bg-red-500/10"
              >
                <Trash2 className="size-4" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <button
        type="button"
        className={cn(iconBtn, 'hidden sm:inline-flex')}
        aria-label={expanded ? 'Shrink panel' : 'Full screen'}
        onClick={onToggleExpand}
      >
        {expanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
      </button>
      <button type="button" className={iconBtn} aria-label="Close" onClick={onClose}>
        <X className="size-4" />
      </button>
    </div>
  )
}

function MenuAction({ action, onSelect }: { action: SheetAction; onSelect: () => void }) {
  const Icon = actionIcon(action.icon)
  return (
    <DropdownMenuItem
      onSelect={onSelect}
      className={cn(
        'gap-2 rounded-full px-3 py-2 font-semibold',
        action.danger
          ? 'text-red-600 focus:bg-red-50 focus:text-red-600 dark:text-red-400 dark:focus:bg-red-500/10'
          : 'text-neutral-800 focus:bg-neutral-100 dark:text-neutral-200 dark:focus:bg-neutral-800'
      )}
    >
      <Icon className="size-4" /> {action.label}
    </DropdownMenuItem>
  )
}

function CompactHeader({
  data,
  canEdit,
  onAction,
  onEdit,
  onDelete
}: {
  data: RecordSheetData
  canEdit: boolean
  onAction: (a: SheetAction) => void
  onEdit: () => void
  onDelete: () => void
}) {
  const general = data.actions.filter((a) => !a.tab)
  const safe = general.filter((a) => !a.danger)
  const primary = safe.slice(0, PRIMARY_ACTIONS)
  const more = safe.slice(PRIMARY_ACTIONS)
  const danger = general.filter((a) => a.danger)
  const photo = data.avatar ?? data.cover

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        {photo && (
          <div className="size-12 shrink-0 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
            <Media photo={photo} className="size-full" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold leading-snug text-neutral-900 dark:text-white">{data.title}</h2>
          {data.badges.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {data.badges.map((b) => (
                <span
                  key={b.label}
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                    TONE_CLASS[b.tone] ?? TONE_CLASS.neutral
                  )}
                >
                  {b.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {(primary.length > 0 || canEdit || more.length > 0 || danger.length > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {primary.map((a, i) => {
            const Icon = actionIcon(a.icon)
            return i === 0 ? (
              <button key={a.id} type="button" className={btnPrimary} onClick={() => onAction(a)}>
                <Icon className="size-4" /> {a.label}
              </button>
            ) : (
              <ActionButton key={a.id} action={a} onClick={() => onAction(a)} />
            )
          })}
          {canEdit && (
            <button type="button" className={btnSecondary} onClick={onEdit}>
              <Pencil className="size-4" /> Edit
            </button>
          )}
          {(more.length > 0 || danger.length > 0 || canEdit) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={btnSecondary}>
                  More <ChevronDown className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-64 rounded-[36px] border border-neutral-200 bg-white p-2 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900"
              >
                {more.map((a) => (
                  <MenuAction key={a.id} action={a} onSelect={() => onAction(a)} />
                ))}
                {more.length > 0 && (
                  <DropdownMenuSeparator className="my-1.5 bg-neutral-200 dark:bg-neutral-800" />
                )}
                {danger.map((a) => (
                  <MenuAction key={a.id} action={a} onSelect={() => onAction(a)} />
                ))}
                {canEdit && (
                  <DropdownMenuItem
                    onSelect={onDelete}
                    className="gap-2 rounded-full px-3 py-2 font-semibold text-red-600 focus:bg-red-50 focus:text-red-600 dark:text-red-400 dark:focus:bg-red-500/10"
                  >
                    <Trash2 className="size-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </div>
  )
}

function CompactSkeleton() {
  return (
    <div className="mx-auto max-w-3xl space-y-4 px-5 pt-6 sm:px-7">
      <div className="h-6 w-2/3 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
      <div className="h-9 w-1/2 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
      <div className="h-40 animate-pulse rounded-[24px] bg-neutral-100 dark:bg-neutral-800" />
      <div className="h-28 animate-pulse rounded-[24px] bg-neutral-100 dark:bg-neutral-800" />
    </div>
  )
}

// ---------------------------------------------------------------------------

function Media({ photo, className }: { photo: SheetPhoto; className?: string }) {
  return photo.video ? (
    <video src={photo.url} muted loop autoPlay playsInline className={cn('object-cover', className)} />
  ) : (
    <img src={photo.url} alt="" loading="lazy" className={cn('object-cover', className)} />
  )
}

function Hero({ data }: { data: RecordSheetData }) {
  return (
    <div className="overflow-hidden rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
      {data.cover ? (
        <Media photo={data.cover} className="h-44 w-full sm:h-56" />
      ) : (
        <div className="h-20 bg-neutral-100 dark:bg-neutral-800" />
      )}
      <div className="flex flex-col gap-4 px-6 pb-6 sm:flex-row sm:items-end">
        <div className="-mt-12 size-24 shrink-0 overflow-hidden rounded-full border-4 border-white bg-neutral-900 dark:border-neutral-900 dark:bg-white">
          {data.avatar ? (
            <Media photo={data.avatar} className="size-full" />
          ) : (
            <span className="flex size-full items-center justify-center text-3xl font-bold text-white dark:text-black">
              {data.title.trim().charAt(0).toUpperCase() || '#'}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">{data.title}</h2>
          {data.subtitle && (
            <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">{data.subtitle}</p>
          )}
          {data.badges.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {data.badges.map((b) => (
                <span
                  key={b.label}
                  className={cn(
                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold',
                    TONE_CLASS[b.tone] ?? TONE_CLASS.neutral
                  )}
                >
                  {b.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

function Sections({ sections }: { sections: SheetSection[] }) {
  const isWide = (s: SheetSection) => s.kind === 'text' || s.kind === 'photos' || s.kind === 'tags'
  const wide = sections.filter(isWide)
  const cards = sections.filter((s) => !isWide(s))
  return (
    <>
      {wide.map((s) => (
        <SectionCard key={s.title} section={s} />
      ))}
      {cards.length > 0 && (
        <div className="gap-5 lg:columns-2 [&>*]:mb-5 [&>*]:break-inside-avoid">
          {cards.map((s) => (
            <SectionCard key={s.title} section={s} />
          ))}
        </div>
      )}
    </>
  )
}

function SectionCard({ section, compact = false }: { section: SheetSection; compact?: boolean }) {
  const { openRecord } = useRecordSheet()
  return (
    <section className={compact ? block : card}>
      <h3
        className={cn(
          'font-bold text-neutral-900 dark:text-white',
          compact ? 'mb-3 text-sm' : 'mb-4 text-base'
        )}
      >
        {section.title}
      </h3>

      {section.kind === 'text' && (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-700 dark:text-neutral-300">
          {section.text}
        </p>
      )}

      {section.kind === 'photos' && (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {section.items.map((p) => (
            <a
              key={p.url}
              href={p.url}
              target="_blank"
              rel="noreferrer"
              className="aspect-square overflow-hidden rounded-[36px] bg-neutral-100 transition-opacity hover:opacity-90 dark:bg-neutral-800"
            >
              <Media photo={p} className="size-full" />
            </a>
          ))}
        </div>
      )}

      {section.kind === 'tags' && (
        <div className="flex flex-wrap gap-2">
          {section.items.map((t) => (
            <span
              key={t}
              className="rounded-full bg-neutral-100 px-3 py-1 text-sm font-medium text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
            >
              {t}
            </span>
          ))}
        </div>
      )}

      {section.kind === 'toggles' && (
        <ul className="space-y-3">
          {section.items.map((t) => (
            <li key={t.label} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-neutral-700 dark:text-neutral-300">{t.label}</span>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold',
                  t.on ? TONE_CLASS.success : TONE_CLASS.neutral
                )}
              >
                <span className={cn('size-1.5 rounded-full', t.on ? 'bg-emerald-500' : 'bg-neutral-400')} />
                {t.on ? 'On' : 'Off'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {section.kind === 'facts' &&
        (section.items.length === 0 ? (
          <p className="text-sm text-neutral-400">{section.empty ?? 'Nothing yet.'}</p>
        ) : (
          <dl className="space-y-3">
            {section.items.map((f) => (
              <div key={`${f.label}-${f.value}`} className="flex items-start justify-between gap-4 text-sm">
                <dt className="shrink-0 text-neutral-500 dark:text-neutral-400">{f.label}</dt>
                <dd className="min-w-0 text-right font-medium text-neutral-900 dark:text-neutral-100">
                  {f.record ? (
                    <button
                      type="button"
                      onClick={() => f.record && openRecord(f.record.key, f.record.id)}
                      className="inline-flex items-center gap-1 font-semibold underline-offset-4 hover:underline"
                    >
                      {f.value} <ArrowUpRight className="size-3.5" />
                    </button>
                  ) : f.href ? (
                    <a
                      href={f.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex max-w-full items-center gap-1 break-all font-semibold underline-offset-4 hover:underline"
                    >
                      {f.value} <ArrowUpRight className="size-3.5 shrink-0" />
                    </a>
                  ) : (
                    <span className="break-words">{f.value}</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ))}
    </section>
  )
}

// ---------------------------------------------------------------------------

const ACTION_WORDS: Record<string, string> = {
  update: 'Edited details',
  create: 'Created',
  delete: 'Deleted',
  reveal: 'Viewed a hidden field'
}

function History({ data }: { data: RecordSheetData }) {
  const [open, setOpen] = useState(false)
  return (
    <section className="overflow-hidden rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-5 py-4 text-left"
      >
        <ChevronDown className={cn('size-4 text-neutral-400 transition-transform', !open && '-rotate-90')} />
        <span className="text-base font-bold text-neutral-900 dark:text-white">What admins changed</span>
        <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
          {data.history.length}
        </span>
      </button>
      {open && (
        <div className="border-t border-neutral-200 dark:border-neutral-800">
          {data.history.length === 0 ? (
            <p className="px-5 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
              No changes made from the admin panel yet.
            </p>
          ) : (
            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {data.history.map((h) => {
                const verb = h.action.split('.').pop() ?? ''
                return (
                  <li key={h.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                    <span className="min-w-0">
                      <span className="font-semibold text-neutral-900 dark:text-white">
                        {ACTION_WORDS[verb] ?? verb.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())}
                      </span>
                      <span className="text-neutral-500 dark:text-neutral-400">
                        {' '}by @{h.actor || 'unknown'}
                        {typeof h.metadata?.reason === 'string' && h.metadata.reason
                          ? ` — “${h.metadata.reason}”`
                          : ''}
                      </span>
                    </span>
                    <time className="shrink-0 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
                      {new Date(h.created_at).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </time>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

function ProfileSkeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-5 p-5 sm:p-8">
      <div className="h-64 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800" />
      <div className="h-24 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800" />
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="h-48 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800" />
        <div className="h-48 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800" />
      </div>
    </div>
  )
}
